// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// What a ramp is to a ball on the playfield underneath it.
//
// A ramp is a layer, and a ball only touches geometry on its own layer — which is right where the
// ramp is high, because a ball on the playfield rolls underneath it. It was wrong where the ramp
// is **low**: at the mouth the plastic sits on the wood, and a ball on the playfield met nothing
// there at all. It rolled straight through the side of the ramp, and through the underside from
// behind, and the only direction a ramp answered was the one a ball entered by (reported as
// *"the slides are not physically responding when a ball hits the sides or bottom (never
// worked)"*).
//
// So a ramp also stands a **footprint** on the playfield layer, along exactly the stretch that is
// too low to pass under: its own two side lines, and a cross-piece at the point where the floor
// rises clear — the underside a ball arriving from behind strikes. The mouth stays open. Outside
// that stretch nothing changes, and the ramp's own layer is not touched.
//
// "Too low" is a floor height below `clearance`, and the point where a run stops being too low is
// found **between** samples, by linear interpolation on the two samples either side of it. Using
// the first clear sample instead put the cross-piece up to a sample-spacing beyond where the ramp
// is actually low enough to hit — 4 cm on a two-point ramp — so a ball rolling under a ramp that
// plainly cleared it struck an invisible wall.
//
// The playfield is layer 0 and its floor is at z = 0 in every machine (a ramp's `leave` already
// relies on that), so the footprint is always on layer 0 whichever layers the ramp connects.
//
// Built once, at load. Only + − × ÷ and √ on the path's own samples, with the same offset formula
// as `Path.offsetPolyline`, so the footprint's side lines lie exactly on the ramp's own walls.

import type { Collider } from '../colliders'
import { makeSegment } from '../colliders'
import type { MaterialName } from '../materials'
import type { Path } from '../Path'

export interface FootprintOptions {
  /** Half the distance between the two side lines, metres. */
  readonly half: number
  /** A floor height below this is too low for a playfield ball to pass under. */
  readonly clearance: number
  readonly material: MaterialName
  readonly device: number
}

/**
 * The playfield-layer colliders for the low stretches of `path`.
 *
 * Order is part of the contract (both cores build the same list): for each sample interval in
 * turn, its left side line, its right side line, then — where the interval contains the point a
 * low run rises clear — the cross-piece there.
 */
export function playfieldFootprint(path: Path, options: FootprintOptions): Collider[] {
  const n = path.xs.length
  const xs = path.xs
  const ys = path.ys
  const zs = path.zs
  const half = options.half
  const clear = options.clearance

  const valid = new Uint8Array(n)
  const lx = new Float64Array(n)
  const ly = new Float64Array(n)
  const rx = new Float64Array(n)
  const ry = new Float64Array(n)
  for (let i = 0; i < n; i++) {
    const j = i === n - 1 ? i - 1 : i
    const ex = xs[j + 1]! - xs[j]!
    const ey = ys[j + 1]! - ys[j]!
    const len = Math.sqrt(ex * ex + ey * ey)
    if (len < 1e-12) continue
    valid[i] = 1
    lx[i] = xs[i]! + (-ey / len) * half
    ly[i] = ys[i]! + (ex / len) * half
    rx[i] = xs[i]! + (-ey / len) * -half
    ry[i] = ys[i]! + (ex / len) * -half
  }

  const tag = { device: options.device, layer: 0 }
  const out: Collider[] = []
  const push = (ax: number, ay: number, bx: number, by: number): void => {
    if (ax === bx && ay === by) return
    out.push(makeSegment(ax, ay, bx, by, options.material, tag))
  }

  for (let i = 0; i + 1 < n; i++) {
    const lowA = zs[i]! < clear
    const lowB = zs[i + 1]! < clear
    if (!lowA && !lowB) continue
    if (valid[i] === 0 || valid[i + 1] === 0) continue

    if (lowA && lowB) {
      push(lx[i]!, ly[i]!, lx[i + 1]!, ly[i + 1]!)
      push(rx[i]!, ry[i]!, rx[i + 1]!, ry[i + 1]!)
      continue
    }

    // One end low, one clear: the run ends inside this interval, at t along it.
    const t = (clear - zs[i]!) / (zs[i + 1]! - zs[i]!)
    const clx = lx[i]! + (lx[i + 1]! - lx[i]!) * t
    const cly = ly[i]! + (ly[i + 1]! - ly[i]!) * t
    const crx = rx[i]! + (rx[i + 1]! - rx[i]!) * t
    const cry = ry[i]! + (ry[i + 1]! - ry[i]!) * t
    if (lowA) {
      push(lx[i]!, ly[i]!, clx, cly)
      push(rx[i]!, ry[i]!, crx, cry)
    } else {
      push(clx, cly, lx[i + 1]!, ly[i + 1]!)
      push(crx, cry, rx[i + 1]!, ry[i + 1]!)
    }
    push(clx, cly, crx, cry)
  }
  return out
}
